import { createContext, type ReactNode, useContext, useEffect, useState } from "react";

interface LayoutContextProps {
  pageTitle: string | null;
  setPageTitle: (title: string | null) => void;
  activeTestStatus: string | null;
  setActiveTestStatus: (status: string | null) => void;
}

const LayoutContext = createContext<LayoutContextProps | undefined>(undefined);

export const LayoutProvider = ({ children }: { children: ReactNode }) => {
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const [activeTestStatus, setActiveTestStatus] = useState<string | null>(null);

  return (
    <LayoutContext.Provider value={{ pageTitle, setPageTitle, activeTestStatus, setActiveTestStatus }}>
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayout must be used within a LayoutProvider");
  }
  return context;
};

export const usePageTitle = (title: string | undefined | null) => {
  const { setPageTitle } = useLayout();

  useEffect(() => {
    if (title) {
      setPageTitle(title);
    }
    return () => {
      setPageTitle(null);
    };
  }, [title, setPageTitle]);
};

export const useActiveTestStatus = (status: string | undefined | null) => {
  const { setActiveTestStatus } = useLayout();

  useEffect(() => {
    if (status) {
      setActiveTestStatus(status);
    }
    return () => {
      setActiveTestStatus(null);
    };
  }, [status, setActiveTestStatus]);
};
